import { Link } from "react-router-dom";
import { ArrowRight, HandHeart, Users, LifeBuoy, HeartHandshake, Sparkles, Compass, Sprout } from "lucide-react";
import { useEffect, useState } from "react";
import { PROGRAMS } from "@/data/programs";
import { api } from "@/lib/api";

const CORE_VALUES = ["Faith", "Restoration", "Integrity", "Dignity", "Community", "Servanthood", "Courage"];

const CTAS = [
  { to: "/volunteer", label: "Get Involved", Icon: Users, testid: "hero-cta-involved" },
  { to: "/volunteer", label: "Become a Mentor", Icon: HeartHandshake, testid: "hero-cta-mentor" },
  { to: "/get-help", label: "Request Support", Icon: LifeBuoy, testid: "hero-cta-support" },
  { to: "/donate", label: "Donate", Icon: HandHeart, testid: "hero-cta-donate" },
];

export default function Home() {
  const [stats, setStats] = useState(null);
  useEffect(() => {
    api.get("/impact-stats").then((r) => setStats(r.data)).catch(() => {});
  }, []);

  return (
    <div data-testid="home-page">
      {/* HERO */}
      <section className="relative overflow-hidden">
        <div className="mx-auto grid max-w-7xl grid-cols-1 gap-12 px-6 pb-20 pt-14 md:px-10 md:pt-24 lg:grid-cols-12 lg:gap-16">
          <div className="lg:col-span-7">
            <div className="inline-flex items-center gap-2 rounded-full border border-clay/20 bg-oat-100 px-4 py-1.5">
              <Sparkles className="h-3.5 w-3.5 text-clay" />
              <span className="text-xs font-bold uppercase tracking-[0.22em] text-clay">
                KEYZ Foundation Inc. · Faith-based nonprofit
              </span>
            </div>
            <h1 className="mt-7 font-serif text-5xl leading-[1.05] tracking-tight text-ink md:text-6xl lg:text-7xl">
              Restoring Lives.
              <br />
              <span className="italic text-clay">Building Leaders.</span>
              <br />
              Strengthening Communities.
            </h1>
            <p className="mt-7 max-w-xl text-lg leading-relaxed text-ink-muted">
              Every soul carries a key. Through mentorship, restoration and radical community, we help unlock
              purpose in men, women, youth and families — one relationship at a time.
            </p>

            <div className="mt-10 flex flex-wrap gap-3">
              {CTAS.map(({ to, label, Icon, testid }) => (
                <Link key={label} to={to} data-testid={testid} className={label === "Donate" ? "btn-primary" : "btn-outline"}>
                  <Icon className="h-4 w-4" />
                  {label}
                </Link>
              ))}
            </div>

            <div className="mt-14 flex items-center gap-6 text-sm text-ink-muted">
              <div className="flex -space-x-2">
                {[
                  "https://images.unsplash.com/photo-1544005313-94ddf0286df2?w=100&auto=format&fit=crop",
                  "https://images.unsplash.com/photo-1519345182560-3f2917c472ef?w=100&auto=format&fit=crop",
                  "https://images.unsplash.com/photo-1503023345310-bd7c1de61c7d?w=100&auto=format&fit=crop",
                  "https://images.unsplash.com/photo-1758613171176-ea64579c2dcf?w=100&auto=format&fit=crop",
                ].map((src, i) => (
                  <img key={i} src={src} alt="Community member" className="h-9 w-9 rounded-full border-2 border-oat object-cover" />
                ))}
              </div>
              <div>
                <span className="font-semibold text-ink">{stats?.lives_restored ?? "342"}+ lives restored</span> and counting.
              </div>
            </div>
          </div>

          <div className="relative lg:col-span-5">
            <div className="relative aspect-[4/5] overflow-hidden rounded-[2rem] border border-clay/15 shadow-sm">
              <img
                src="https://images.pexels.com/photos/1198171/pexels-photo-1198171.jpeg?auto=compress&cs=tinysrgb&w=1200"
                alt="Diverse hands stacked in unity"
                className="h-full w-full object-cover"
              />
            </div>
            <div className="absolute -bottom-6 -left-6 hidden max-w-[240px] rounded-2xl border border-clay/15 bg-white p-5 shadow-sm md:block">
              <div className="eyebrow">Our promise</div>
              <p className="mt-2 font-serif text-lg leading-snug text-ink">
                "No one walks the road of restoration alone."
              </p>
            </div>
            <div className="absolute -top-6 -right-4 hidden rotate-3 rounded-2xl border border-sage/25 bg-sage px-4 py-3 text-oat shadow-sm md:block">
              <div className="text-xs font-bold uppercase tracking-[0.22em]">Since 2015</div>
              <div className="font-serif text-xl">11 years of restoration</div>
            </div>
          </div>
        </div>

        {/* Values marquee */}
        <div className="border-y border-clay/15 bg-oat-100 py-5">
          <div className="mx-auto flex max-w-full overflow-hidden">
            <div className="flex shrink-0 animate-marquee items-center gap-10 pr-10">
              {[...CORE_VALUES, ...CORE_VALUES].map((v, i) => (
                <span key={i} className="flex items-center gap-3 whitespace-nowrap font-serif text-2xl italic text-clay">
                  {v}
                  <span className="h-1.5 w-1.5 rounded-full bg-clay/50" />
                </span>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* MISSION SPLIT */}
      <section className="section">
        <div className="container-editorial grid grid-cols-1 gap-14 lg:grid-cols-12">
          <div className="lg:col-span-5">
            <div className="eyebrow">Our mission</div>
            <h2 className="mt-4 font-serif text-4xl leading-tight text-ink md:text-5xl">
              We believe restoration is done shoulder-to-shoulder.
            </h2>
          </div>
          <div className="lg:col-span-7 lg:pt-4">
            <p className="text-lg leading-relaxed text-ink-muted">
              KEYZ Foundation exists to walk with people through the hardest chapters of their story — reentry,
              addiction, single-parenthood, adolescence, homelessness — and help them write a new one. Our four
              flagship programs pair every participant with mentors, resources and a community that stays.
            </p>
            <div className="mt-10 grid grid-cols-1 gap-4 sm:grid-cols-3">
              {[
                { Icon: Sprout, label: "Rooted in faith", body: "Christ-centered but never coercive." },
                { Icon: Compass, label: "Guided by dignity", body: "Every story matters. Every person is seen." },
                { Icon: HeartHandshake, label: "Bound in community", body: "We are stronger together, always." },
              ].map(({ Icon, label, body }) => (
                <div key={label} className="rounded-2xl border border-clay/10 bg-white p-5">
                  <Icon className="h-5 w-5 text-clay" />
                  <div className="mt-3 font-serif text-lg text-ink">{label}</div>
                  <p className="mt-1 text-sm text-ink-muted">{body}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* PROGRAMS */}
      <section className="bg-oat-100">
        <div className="section container-editorial">
          <div className="flex flex-col justify-between gap-6 md:flex-row md:items-end">
            <div>
              <div className="eyebrow">Four pathways</div>
              <h2 className="mt-3 font-serif text-4xl leading-tight text-ink md:text-5xl">Programs that meet people where they are.</h2>
            </div>
            <Link to="/programs" data-testid="home-programs-view-all" className="btn-outline w-fit">
              Explore all programs <ArrowRight className="h-4 w-4" />
            </Link>
          </div>

          <div className="mt-14 grid grid-cols-1 gap-6 md:grid-cols-2">
            {PROGRAMS.map((p) => (
              <Link
                key={p.slug}
                to={`/programs`}
                data-testid={`home-program-${p.slug}`}
                className="group relative overflow-hidden rounded-3xl border border-clay/15 bg-white transition-shadow duration-300 hover:shadow-md"
              >
                <div className="aspect-[16/10] overflow-hidden">
                  <img
                    src={p.image}
                    alt={p.name}
                    className="h-full w-full object-cover transition-transform duration-700 group-hover:scale-105"
                  />
                </div>
                <div className="p-7">
                  <div className="flex items-center justify-between">
                    <h3 className="font-serif text-2xl text-ink">{p.name}</h3>
                    <ArrowRight className="h-4 w-4 -translate-x-1 text-clay transition-transform duration-300 group-hover:translate-x-0" />
                  </div>
                  <p className="mt-1.5 text-sm italic text-clay">{p.tagline}</p>
                  <p className="mt-3 text-sm text-ink-muted">{p.description}</p>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* IMPACT STRIP */}
      <section className="section">
        <div className="container-editorial">
          <div className="eyebrow">Annual impact</div>
          <h2 className="mt-3 font-serif text-4xl leading-tight text-ink md:text-5xl">Numbers we hold sacred.</h2>

          <div className="mt-12 grid grid-cols-2 gap-6 md:grid-cols-4">
            {[
              { label: "Lives restored", v: stats?.lives_restored ?? 342 },
              { label: "Active mentors", v: stats?.active_mentors ?? 87 },
              { label: "Youth served", v: stats?.youth_served ?? 512 },
              { label: "Families reunited", v: stats?.families_reunited ?? 46 },
            ].map((s) => (
              <div key={s.label} className="rounded-2xl border border-clay/10 bg-white p-6">
                <div className="font-serif text-5xl text-clay">{s.v}</div>
                <div className="mt-2 text-sm font-semibold uppercase tracking-wider text-ink-muted">{s.label}</div>
              </div>
            ))}
          </div>

          <div className="mt-10 flex justify-start">
            <Link to="/impact" data-testid="home-view-impact" className="btn-ghost">
              View full impact dashboard <ArrowRight className="h-4 w-4" />
            </Link>
          </div>
        </div>
      </section>

      {/* GET HELP CTA */}
      <section className="bg-sage text-oat">
        <div className="section container-editorial grid grid-cols-1 items-center gap-10 lg:grid-cols-12">
          <div className="lg:col-span-8">
            <div className="text-xs font-bold uppercase tracking-[0.22em] text-oat/70">Confidential support</div>
            <h2 className="mt-3 font-serif text-4xl leading-tight md:text-5xl">Need someone in your corner? Reach out.</h2>
            <p className="mt-4 max-w-2xl text-oat/85">
              Mentor. Food. Career. Family. Mental health. Prayer. Or something you can't quite name yet.
              Fill out the confidential form — a real human from KEYZ will reach out within 48 hours.
            </p>
          </div>
          <div className="flex flex-wrap gap-3 lg:col-span-4 lg:justify-end">
            <Link to="/get-help" data-testid="home-get-help-cta" className="inline-flex items-center gap-2 rounded-full bg-oat px-6 py-3 font-bold text-sage transition-colors hover:bg-oat-100">
              Request Support <ArrowRight className="h-4 w-4" />
            </Link>
            <Link to="/contact" data-testid="home-contact-cta" className="inline-flex items-center gap-2 rounded-full border-2 border-oat/70 px-6 py-3 font-bold text-oat hover:bg-oat/10">
              Contact us
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}
